import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { isEqual, omit, pick } from "lodash";
import { useRouter } from "next/router";
import type { ObjectSchema } from "yup";
import { asyncVoid } from "utils/asyncVoidHandler";
import { useCurrentBrandId } from "utils/currentUserHooks";
import usePrevious from "utils/usePrevious";
import { Url } from "utils/links";
import useLatestRef from "utils/useLatestRef";
import { getIsNotRefOrLazyDescription } from "utils/schemas/utils";

type ObjectSchemaShape<S> = S extends ObjectSchema<infer T> ? T : never;

type UseGetRouteParamsReturn<Params> = [params?: Params | null, error?: Error];

export function useGetRouteParams<
  Params extends {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [x: string]: any;
  },
  T = ObjectSchemaShape<Params>,
>(
  schema: ObjectSchema<Params>,
  defaultInitial?: MutableRefObject<T | undefined>,
): UseGetRouteParamsReturn<T> {
  const { query, replace, isReady } = useRouter();
  const [returnValue, setReturnValue] = useState<UseGetRouteParamsReturn<T>>();
  const isInitialSetRef = useRef(false);

  useEffect(() => {
    if (!isReady) {
      setReturnValue(undefined);
    } else {
      schema
        .validate(
          isInitialSetRef.current
            ? query
            : { ...defaultInitial?.current, ...query },
        )
        .then((result) => {
          setReturnValue([result as T]);

          // eslint-disable-next-line no-param-reassign
          if (defaultInitial) defaultInitial.current = result as T;

          const schemaFields = schema.fields;
          const arrayFields = Object.keys(schemaFields).filter((n) => {
            const field = schema.fields[n];
            const desc = field.describe();
            return getIsNotRefOrLazyDescription(desc) && desc.type === "array";
          });
          const queryEnsureArray = Object.keys(query).reduce(
            (acc, k) => {
              const v = acc[k];
              if (arrayFields.includes(k) && !Array.isArray(v)) {
                acc[k] = v ? [v] : [];
              }
              return acc;
            },
            { ...query },
          );
          arrayFields.forEach((k) => {
            if (!queryEnsureArray[k]) queryEnsureArray[k] = [];
          });

          if (!isEqual(queryEnsureArray, result))
            asyncVoid(replace({ query: { ...query, ...(result as Params) } }));
        })
        .catch((reason) => {
          if (reason instanceof Error) {
            setReturnValue([undefined, reason]);
          }
        })
        .finally(() => {
          isInitialSetRef.current = true;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, JSON.stringify(query), replace, isReady]);

  return returnValue || [];
}

export function useGetRouteParam(routeParamName: string) {
  const { query } = useRouter();
  const { [routeParamName]: param } = query;
  if (!param) return;
  if (Array.isArray(param)) return param[0];
  return param;
}

type ReplaceRouteParamOptions = {
  keepOtherParams?: boolean | string[];
};

export function useReplaceRouteParam() {
  const router = useRouter();
  return useCallback(
    (
      routeParamName: string,
      newParam: string | string[] | null | undefined,
      options: ReplaceRouteParamOptions = {},
    ) => {
      const { keepOtherParams = true } = options;
      const otherQueryParams =
        (keepOtherParams === true && router.query) ||
        (Array.isArray(keepOtherParams) &&
          pick(router.query, keepOtherParams)) ||
        undefined;

      const newQueryParams = {
        ...otherQueryParams,
        [routeParamName]: newParam,
      };
      if (!newParam || (Array.isArray(newParam) && newParam.length === 0)) {
        delete newQueryParams[routeParamName];
      }
      return router.replace({ query: newQueryParams }, undefined, {
        shallow: true,
      });
    },
    [router],
  );
}

export function useNavigateToRouteParam(
  routeParamName: string,
  options: ReplaceRouteParamOptions = {},
) {
  const latestOptionsRef = useLatestRef(options);
  const replaceRouteParam = useReplaceRouteParam();
  return useCallback(
    (newParam: string | null | undefined) =>
      replaceRouteParam(routeParamName, newParam, latestOptionsRef.current),
    [routeParamName, latestOptionsRef, replaceRouteParam],
  );
}

export function useRouteParam(
  routeParamName: string,
  options?: ReplaceRouteParamOptions,
): [
  ReturnType<typeof useGetRouteParam>,
  ReturnType<typeof useNavigateToRouteParam>,
] {
  const routeParam = useGetRouteParam(routeParamName);
  const navigateToParam = useNavigateToRouteParam(routeParamName, options);
  return [routeParam, navigateToParam];
}

export function useOnBrandChange(onBrandChange: () => void) {
  const router = useRouter();
  const brandId = useCurrentBrandId();
  const prevBrandId = usePrevious(brandId);

  useEffect(() => {
    if (prevBrandId && brandId !== prevBrandId) {
      onBrandChange();
    }
  }, [brandId, prevBrandId, router, onBrandChange]);
}

export function useClearRouteParamsOnBrandChange(routeParamNames: string[]) {
  const router = useRouter();
  useOnBrandChange(
    useCallback(() => {
      if (!routeParamNames.some((p) => router.query?.[p])) return;

      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.info(
          `Clearing route params on brand change: ${routeParamNames.join(
            ", ",
          )}`,
        );
      }

      asyncVoid(router.replace({ query: omit(router.query, routeParamNames) }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeParamNames.join(","), router]),
  );
}

export function useReplaceUrlOnBrandChange(newUrl: Url) {
  const router = useRouter();
  useOnBrandChange(
    useCallback(() => {
      if (
        typeof newUrl === "string" &&
        newUrl === router.pathname &&
        !Object.keys(router.query || {}).length
      )
        return;

      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.info(
          `Replacing pathname on brand change: ${String(
            typeof newUrl === "string" ? newUrl : newUrl.pathname,
          )}`,
        );
      }
      asyncVoid(router.replace(newUrl));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeof newUrl === "string" ? newUrl : JSON.stringify(newUrl), router]),
  );
}

export function getPathnameParams(pathname: string) {
  const matches = pathname.match(/\[([^\]]+)\]/g);
  return matches ? matches.map((match) => match.slice(1, -1)) : [];
}
